import { SB } from '@play-co/replicant';
import {
  createFlexContainer,
  createFlexMessageTemplate,
  createOpenGameAction,
} from './utils';
import type { FlexComponent, FlexSpan } from './types';

export const purchaseConfirmationAssets = {
  'iap/paymentConfirmed/en': 'private/chatbot/iap/payment_confirmed_en.png',
  'iap/paymentConfirmed/ja': 'private/chatbot/iap/payment_confirmed_ja.png',
};

const copy = {
  en: {
    title: 'Payment confirmed!',
    bodyPrefix: 'We added ',
    bodySuffix: ' to your account. Restart the game if you don’t see it yet.',
    cta: 'Open Game',
  },
  ja: {
    title: 'お支払いを確認しました！',
    bodyPrefix: '',
    bodySuffix:
      ' を付与しました。\n反映されない場合はゲームを再起動してください。',
    cta: 'ゲームを開く',
  },
} as const;

const bodyOutlineOffsets = [
  { offsetTop: '-1.5px', offsetStart: '0px' },
  { offsetTop: '1.5px', offsetStart: '0px' },
  { offsetTop: '0px', offsetStart: '-1.5px' },
  { offsetTop: '0px', offsetStart: '1.5px' },
  { offsetTop: '-1px', offsetStart: '-1px' },
  { offsetTop: '-1px', offsetStart: '1px' },
  { offsetTop: '1px', offsetStart: '-1px' },
  { offsetTop: '1px', offsetStart: '1px' },
] as const;

export const purchaseConfirmationMessage = createFlexMessageTemplate(
  SB.object({
    itemName: SB.string(),
    locale: SB.tuple(['en', 'ja']),
  }),
  (data, payload, api) => {
    const localizedCopy = copy[data.locale];
    const bodyText = `${localizedCopy.bodyPrefix}${data.itemName}${localizedCopy.bodySuffix}`;
    const altText = `${localizedCopy.title} ${bodyText.replace(/[\r\n]+/g, ' ')}`;
    const action = createOpenGameAction(localizedCopy.cta, payload);
    const bodyWidth = data.locale === 'en' ? '73%' : '94%';
    const bodyOffsetTop = data.locale === 'en' ? '58.1%' : '58.4%';
    const bodyOffsetStart = data.locale === 'en' ? '13.5%' : '5.6%';
    const bodyMaxLines = data.locale === 'en' ? 3 : 2;
    // LINE doesn't reliably shrink multi-line span content. At 15px, the
    // longest forced Japanese line fits the measured 354px body width.
    const bodyTextSize = '15px';
    // LINE rejects Flex spans whose text is an empty string.
    const foregroundSpans = (
      [
        {
          type: 'span',
          text: localizedCopy.bodyPrefix,
          color: '#FFFFFF',
          size: bodyTextSize,
          weight: 'bold',
        },
        {
          type: 'span',
          text: data.itemName,
          color: '#FEDA4E',
          size: bodyTextSize,
          weight: 'bold',
        },
        {
          type: 'span',
          text: localizedCopy.bodySuffix,
          color: '#FFFFFF',
          size: bodyTextSize,
          weight: 'bold',
        },
      ] satisfies FlexSpan[]
    ).filter(({ text }) => text.length > 0);
    const outlineSpans = foregroundSpans.map((span) => ({
      ...span,
      color: '#000000',
    }));

    return createFlexContainer(altText, {
      type: 'bubble',
      size: 'giga',
      action,
      body: {
        type: 'box',
        layout: 'vertical',
        paddingAll: '0px',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath(`iap/paymentConfirmed/${data.locale}`),
            size: 'full',
            aspectMode: 'cover',
            aspectRatio: '1:1',
            gravity: 'center',
          },
          {
            type: 'box',
            layout: 'vertical',
            position: 'absolute',
            width: bodyWidth,
            height: '18%',
            offsetTop: bodyOffsetTop,
            offsetStart: bodyOffsetStart,
            justifyContent: 'center',
            alignItems: 'center',
            contents: [
              ...bodyOutlineOffsets.map(({ offsetTop, offsetStart }) => ({
                type: 'box',
                layout: 'vertical',
                position: 'absolute',
                width: '100%',
                height: '100%',
                offsetTop: '0px',
                offsetStart: '0px',
                justifyContent: 'center',
                alignItems: 'center',
                contents: [
                  {
                    type: 'text',
                    contents: outlineSpans,
                    weight: 'bold',
                    size: bodyTextSize,
                    align: 'center',
                    gravity: 'center',
                    wrap: true,
                    lineSpacing: '2px',
                    scaling: false,
                    maxLines: bodyMaxLines,
                    position: 'relative',
                    offsetTop,
                    offsetStart,
                  },
                ],
              })),
              {
                type: 'text',
                contents: foregroundSpans,
                weight: 'bold',
                size: bodyTextSize,
                align: 'center',
                gravity: 'center',
                wrap: true,
                lineSpacing: '2px',
                scaling: false,
                maxLines: bodyMaxLines,
              },
            ],
          },
        ] as FlexComponent[],
      },
    });
  },
);
